// Terület-oldal (spec 6.1): kategória-lista, kategóriánként a cikkek. A kategória-szekciók
// horgony-id-t kapnak (a fa és a kenyérmorzsa kategória-linkjei ide ugranak).
// A tartalom a `knowledgeSpace` végpontról jön — a fa csak szerkezetet ad (F0-mérés).
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useStructure } from '@/features/editor/api';
import { useKnowledgeSpace } from '@/features/knowledge/api';
import { CategoryArticles } from '@/features/knowledge/components/category-articles';
import { CategoryCreateArticleLink } from '@/features/knowledge/components/category-create-article-link';
import { CategoryDeleteButton } from '@/features/knowledge/components/category-delete-button';
import { CategoryStructureActions } from '@/features/knowledge/components/category-structure-actions';
import { KnowledgeLayout } from '@/features/knowledge/components/knowledge-layout';
import { SpaceStructureActions } from '@/features/knowledge/components/space-structure-actions';
import { StateIcons } from '@/features/knowledge/components/state-icons';
import { useTopic } from '@/features/knowledge/hooks/use-topic';
import { splitSpaceName } from '@/features/knowledge/lib/grouping';
import { requireAuth } from '@/lib/auth';
import { useHashScroll } from '@/lib/use-hash-scroll';
import { useHasPermission } from '@/lib/use-permission';

export const Route = createFileRoute('/hub/$spaceSlug/')({
  // A szerkesztés-mód az URL-ben él (`?szerkesztes=1`), ugyanazon a néven, mint a Tudástár fájában:
  // így megosztható, újratöltésre megmarad, és a két felület ugyanazt a szót használja rá.
  validateSearch: (search: Record<string, unknown>): { szerkesztes?: '1' } =>
    // A router a `?szerkesztes=1`-et SZÁMKÉNT parse-olja, ezért string-összehasonlítás kell.
    String(search.szerkesztes) === '1' ? { szerkesztes: '1' as const } : {},
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: SpacePage,
});

function SpacePage(): JSX.Element {
  const { t } = useTranslation();
  const { spaceSlug } = Route.useParams();
  const { szerkesztes } = Route.useSearch();
  const navigate = useNavigate();
  const query = useKnowledgeSpace(spaceSlug);
  const { icon } = useTopic();
  const page = query.data?.status === 200 ? query.data.body : null;
  // Megosztott/újratöltött könyv-link (`#<könyv>`): a szakasz csak a tartalommal együtt jön létre.
  useHashScroll(page !== null);

  const canManageCategories = useHasPermission(PERMISSIONS.categoryManage);
  const canManageSpace = useHasPermission(PERMISSIONS.spaceManage);
  const canDeleteSpace = useHasPermission(PERMISSIONS.spaceDelete);
  const canDeleteCategories = useHasPermission(PERMISSIONS.categoryDelete);
  // A cikk-létrehozás kapuja SZÁNDÉKOSAN a szervertől mért `article.manage`, nem a `category.manage`
  // (ami a KÖNYV kezelését — átnevezés, törlés — fedi). A két jog különböző szerep is lehet.
  const canCreateArticle = useHasPermission(PERMISSIONS.articleManage);
  const canPublishPublic = useHasPermission(PERMISSIONS.articlePublishPublic);
  const canEditStructure =
    canManageCategories || canManageSpace || canDeleteSpace || canDeleteCategories || canPublishPublic;
  // A mód CSAK joggal kapcsolható be — egy URL-ben hozott `?szerkesztes=1` sem ad műveletet
  // annak, akinek nincs joga (a valódi kapu a BFF, ez a felület fegyelme).
  const editMode = szerkesztes === '1' && canEditStructure;

  // A szerkezet-forrás CSAK szerkezet-joggal indul — az olvasók 90%-a nem szerkeszt, nekik ez egy
  // fölösleges (élesben ~35 KB) kérés lenne. Ez adja a `version`-t (OCC az archiváláshoz) és a
  // valódi darabszámokat (archivált könyv, kukás cikk) a törlés-blokkolóhoz.
  const structure = useStructure({ enabled: canEditStructure });
  const structureBody = structure.data?.status === 200 ? structure.data.body : null;
  const editSpace = structureBody?.spaces.find((s) => s.slug === spaceSlug) ?? null;

  if (query.isLoading) {
    return (
      <KnowledgeLayout activeSpaceSlug={spaceSlug}>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-40 w-full" />
      </KnowledgeLayout>
    );
  }

  if (!page) {
    // a ts-rest a 4xx-et hibaként adja vissza (nem `data`-ként)
    const notFound = (query.error as { status?: number } | null)?.status === 404;
    return (
      <KnowledgeLayout activeSpaceSlug={spaceSlug}>
        <p className={notFound ? 'text-muted-foreground text-sm' : 'text-destructive text-sm'}>
          {notFound ? t('knowledge.notFound') : t('knowledge.loadError')}
        </p>
        <Link
          to="/tudastar"
          className="text-primary mt-4 inline-block text-sm underline underline-offset-2"
        >
          {t('knowledge.backHome')}
        </Link>
      </KnowledgeLayout>
    );
  }

  const { space, categories } = page;
  const { prefix, label } = splitSpaceName(space.name);
  const Icon = icon(space.slug);

  return (
    <KnowledgeLayout activeSpaceSlug={spaceSlug}>
      <KnowledgeCrumbs path={{ space: { slug: space.slug, name: space.name } }} />

      {/* A fejléc ikon-mezője ugyanaz, mint a kezdőlapi kártyáé — ez köti össze a kettőt: ahonnan
          elindultál, azt a jelet látod itt is. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <header className="flex max-w-[68ch] items-start gap-4">
          <span className="bg-topic/[0.09] flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
            <Icon className="text-topic h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            {prefix && (
              <span className="text-topic text-2xs block font-semibold uppercase tracking-[0.12em]">
                {prefix}
              </span>
            )}
            <h1 className="text-read-2xl flex items-center gap-2 font-semibold tracking-tight">
              {label}
              <StateIcons publiclyVisible={space.publiclyVisible} />
            </h1>
            {space.description && (
              <p className="text-muted-foreground text-read-sm mt-2">{space.description}</p>
            )}
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-1">
          <SpaceStructureActions
            spaceId={space.id}
            spaceName={space.name}
            edit={editSpace}
            canManageCategories={canManageCategories}
            canManageSpace={canManageSpace}
            canDeleteSpace={canDeleteSpace}
            canPublishPublic={canPublishPublic}
            editMode={editMode}
          />

          {/* A KAPCSOLÓ csak szerkezet-joggal jelenik meg — az olvasónak egy tiltott gomb csak zaj.
              Az állapot az URL-ben él, tehát a „szerkesztek ezen a szekción" nézet megosztható. */}
          {canEditStructure && (
            <Button
              type="button"
              size="sm"
              variant={editMode ? 'secondary' : 'outline'}
              onClick={() => {
                void navigate({
                  to: '/hub/$spaceSlug',
                  params: { spaceSlug },
                  search: editMode ? {} : { szerkesztes: '1' as const },
                  replace: true,
                });
              }}
            >
              {editMode ? t('structure.editModeOn') : t('structure.editMode')}
            </Button>
          )}
        </div>
      </div>

      {/* ÜRES SZEKCIÓ: itt nem elég közölni, hogy nincs könyv — a következő lépést is fel kell
          ajánlani. Élesben ez volt a blokkoló: a felhasználó belépett az üres szekcióba, és onnan
          semmi nem vezetett tovább (a cikkhez ugyanis előbb könyv kell). */}
      {categories.length === 0 && (
        <div className="mt-10 space-y-3">
          <p className="text-muted-foreground text-sm">{t('knowledge.emptySpace')}</p>
          <SpaceStructureActions
            spaceId={space.id}
            spaceName={space.name}
            edit={editSpace}
            canManageCategories={canManageCategories}
            canManageSpace={canManageSpace}
            canDeleteSpace={canDeleteSpace}
            canPublishPublic={canPublishPublic}
            variant="empty"
          />
        </div>
      )}

      <div className="mt-10 space-y-12">
        {categories.map((category) => (
          <CategoryArticles
            key={category.id}
            spaceSlug={space.slug}
            categorySlug={category.slug}
            categoryName={category.name}
            categoryPubliclyVisible={category.publiclyVisible}
            articles={category.articles}
            actions={
              <>
                <CategoryCreateArticleLink
                  spaceId={space.id}
                  categoryId={category.id}
                  canCreateArticle={canCreateArticle}
                />
                {editMode && (
                  <CategoryStructureActions
                    spaceId={space.id}
                    edit={editSpace?.categories.find((c) => c.id === category.id) ?? null}
                    publishedCount={
                      category.articles.filter((a) => a.status === 'published').length
                    }
                    canManageCategories={canManageCategories}
                    canPublishPublic={canPublishPublic}
                  />
                )}
                {canDeleteCategories && (
                  <CategoryDeleteButton
                    categoryId={category.id}
                    categoryName={category.name}
                    edit={editSpace?.categories.find((c) => c.id === category.id) ?? null}
                  />
                )}
              </>
            }
            emptyAction={
              <CategoryCreateArticleLink
                spaceId={space.id}
                categoryId={category.id}
                canCreateArticle={canCreateArticle}
                variant="empty"
              />
            }
          />
        ))}
      </div>
    </KnowledgeLayout>
  );
}
